import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "react-toastify";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import styles from "./AllEnquiries.module.scss";
import DateRangeFilter, { getPresetRange } from "./marketing/DateRangeFilter";
import { getProgramsAnalytics } from "../Components/enquiryProfileApi";
import { RoleContext } from "../Contexts/RoleContext";

const INTERVALS = ["day", "week", "month"];
const PALETTE = ["#2563eb", "#7c3aed", "#059669", "#ea580c", "#db2777", "#0891b2", "#65a30d", "#f59e0b"];
const TOP_N_OPTIONS = [5, 10, 20, 50, "all"];
const CHART_CARD_SX = {
  borderRadius: 3,
  borderColor: "rgba(148,163,184,0.28)",
  boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
  background: "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.92) 100%)",
};

function toSeriesRows(rows = []) {
  const grouped = new Map();
  const names = new Set();
  for (const row of rows) {
    const bucket = row.bucket || "Unknown";
    const name = row.name || "Unknown";
    if (!grouped.has(bucket)) grouped.set(bucket, { bucket });
    grouped.get(bucket)[name] = Number(row.count || 0);
    names.add(name);
  }
  return {
    data: Array.from(grouped.values()).sort((a, b) => String(a.bucket).localeCompare(String(b.bucket))),
    names: Array.from(names).sort((a, b) => a.localeCompare(b)),
  };
}

function toCumulativeSeries(seriesRows = [], names = []) {
  const running = {};
  names.forEach((name) => {
    running[name] = 0;
  });
  return seriesRows.map((row) => {
    const next = { bucket: row.bucket };
    names.forEach((name) => {
      const inc = Number(row[name] || 0);
      running[name] = Number(running[name] || 0) + inc;
      next[name] = running[name];
    });
    return next;
  });
}

function roleDefaultFilters(role) {
  const normalized = String(role || "").toLowerCase();
  if (normalized === "manager") {
    return { ...(getPresetRange("This Month") || {}), interval: "week", compare: true, top_n: 10, program_ids: [], course_ids: [] };
  }
  if (normalized === "admin") {
    return { ...(getPresetRange("This Year") || {}), interval: "month", compare: false, top_n: 20, program_ids: [], course_ids: [] };
  }
  return { ...(getPresetRange("This Month") || {}), interval: "day", compare: false, top_n: 10, program_ids: [], course_ids: [] };
}

function abbreviateLabel(label, max = 16) {
  const text = String(label || "");
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}...`;
}

function totalsToMap(rows = []) {
  return rows.reduce((acc, row) => {
    acc[row.name] = Number(row.count || 0);
    return acc;
  }, {});
}

function buildComparisonInsights(current, comparison) {
  if (!comparison) return [];
  const insights = [];
  const currentTotal = Number(current?.summary?.total_registrations || 0);
  const previousTotal = Number(comparison?.summary?.total_registrations || 0);
  const growthPct = previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal) * 100 : null;
  if (growthPct != null) {
    insights.push(
      growthPct >= 0
        ? `Total registrations are up ${growthPct.toFixed(1)}% vs previous period.`
        : `Total registrations are down ${Math.abs(growthPct).toFixed(1)}% vs previous period.`
    );
  }

  const programCurrent = totalsToMap(current?.programs?.totals || []);
  const programPrev = totalsToMap(comparison?.programs?.totals || []);
  let topIncrease = { name: null, delta: -Infinity };
  let topDrop = { name: null, delta: Infinity };
  Object.keys(programCurrent).forEach((name) => {
    const delta = programCurrent[name] - (programPrev[name] || 0);
    if (delta > topIncrease.delta) topIncrease = { name, delta };
    if (delta < topDrop.delta) topDrop = { name, delta };
  });
  if (topIncrease.name && topIncrease.delta > 0) insights.push(`Top growing program: ${topIncrease.name} (+${topIncrease.delta}).`);
  if (topDrop.name && topDrop.delta < 0) insights.push(`Biggest program drop: ${topDrop.name} (${topDrop.delta}).`);

  const topProgram = (current?.programs?.totals || [])[0];
  if (topProgram && currentTotal > 0) {
    const share = (topProgram.count / currentTotal) * 100;
    if (share >= 40) {
      insights.push(`Concentration risk: ${topProgram.name} contributes ${share.toFixed(1)}% of registrations.`);
    }
  }
  return insights;
}

function buildCsvRows(current) {
  const rows = [["type", "bucket", "name", "count"]];
  (current?.programs?.timeline || []).forEach((r) => rows.push(["program_timeline", r.bucket, r.name, r.count]));
  (current?.courses?.timeline || []).forEach((r) => rows.push(["course_timeline", r.bucket, r.name, r.count]));
  (current?.programs?.totals || []).forEach((r) => rows.push(["program_total", "", r.name, r.count]));
  (current?.courses?.totals || []).forEach((r) => rows.push(["course_total", "", r.name, r.count]));
  return rows.map((r) => r.map((x) => `"${String(x ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
}

async function exportChartPng(containerId, fileName) {
  const root = document.getElementById(containerId);
  const svg = root?.querySelector("svg");
  if (!svg) throw new Error("Chart SVG not found");
  const svgData = new XMLSerializer().serializeToString(svg);
  const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  try {
    await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width || 1400;
        canvas.height = img.height || 700;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        const png = canvas.toDataURL("image/png");
        const a = document.createElement("a");
        a.href = png;
        a.download = fileName;
        a.click();
        resolve();
      };
      img.onerror = reject;
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function StyledTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2,
        border: "1px solid rgba(148,163,184,0.35)",
        bgcolor: "rgba(255,255,255,0.98)",
        boxShadow: "0 10px 24px rgba(15,23,42,0.14)",
        minWidth: 180,
      }}
    >
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.75 }}>
        {label}
      </Typography>
      {payload.slice(0, 8).map((entry) => (
        <Stack key={entry.dataKey} direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="caption" sx={{ color: entry.color, fontWeight: 600 }}>
            {entry.dataKey}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {entry.value}
          </Typography>
        </Stack>
      ))}
      {payload.length > 8 && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          +{payload.length - 8} more series
        </Typography>
      )}
    </Box>
  );
}

export default function AcademicsAnalyticsPage() {
  const { role } = useContext(RoleContext);
  const canExport = role === "admin";
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => roleDefaultFilters(role));
  const [analytics, setAnalytics] = useState({
    metadata: { available_programs: [], available_courses: [], elapsed_ms: 0 },
    current: { programs: { timeline: [], totals: [] }, courses: { timeline: [], totals: [] }, summary: { total_registrations: 0 } },
    comparison: null,
  });
  const [programHidden, setProgramHidden] = useState(new Set());
  const [courseHidden, setCourseHidden] = useState(new Set());
  const [programFocus, setProgramFocus] = useState("");
  const [courseFocus, setCourseFocus] = useState("");
  const [fullscreen, setFullscreen] = useState({ open: false, key: "" });
  const [saveName, setSaveName] = useState("");
  const [savedViews, setSavedViews] = useState([]);
  const [selectedViewId, setSelectedViewId] = useState("");

  const viewsStorageKey = `academics_analytics_saved_views_${String(role || "global").toLowerCase()}`;
  const defaultViewKey = `academics_analytics_default_view_${String(role || "global").toLowerCase()}`;

  const trackInteraction = (action) => {
    try {
      const key = "academics_analytics_telemetry";
      const existing = JSON.parse(localStorage.getItem(key) || "{}");
      existing[action] = (existing[action] || 0) + 1;
      localStorage.setItem(key, JSON.stringify(existing));
    } catch (_error) {
      // no-op telemetry failure
    }
  };

  const load = useCallback(async (effective) => {
    if (!effective) return;
    setLoading(true);
    try {
      const started = performance.now();
      const data = await getProgramsAnalytics({
        start_date: effective.start_date,
        end_date: effective.end_date,
        interval: effective.interval || "day",
        compare: Boolean(effective.compare),
        top_n: effective.top_n === "all" ? undefined : effective.top_n,
        program_ids: effective.program_ids || [],
        course_ids: effective.course_ids || [],
      });
      setAnalytics({
        metadata: data?.metadata || { available_programs: [], available_courses: [] },
        current: data?.current || { programs: { timeline: [], totals: [] }, courses: { timeline: [], totals: [] }, summary: { total_registrations: 0 } },
        comparison: data?.comparison || null,
      });
      const measured = Math.round(performance.now() - started);
      setAnalytics((prev) => ({
        ...prev,
        metadata: { ...(prev.metadata || {}), elapsed_ms: Number(prev.metadata?.elapsed_ms || measured) },
      }));
    } catch (error) {
      toast.error(error.message || "Failed to load academics analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    try {
      const loadedViews = JSON.parse(localStorage.getItem(viewsStorageKey) || "[]");
      setSavedViews(Array.isArray(loadedViews) ? loadedViews : []);
      const defaultId = localStorage.getItem(defaultViewKey) || "";
      setSelectedViewId(defaultId);

      const defaultView = (loadedViews || []).find((v) => v.id === defaultId);
      const initial = defaultView?.filters || roleDefaultFilters(role);
      setFilters(initial);
      load(initial);
    } catch (_error) {
      const initial = roleDefaultFilters(role);
      setFilters(initial);
      load(initial);
    }
  }, [defaultViewKey, load, role, viewsStorageKey]);

  const availablePrograms = analytics.metadata?.available_programs || [];
  const availableCoursesAll = analytics.metadata?.available_courses || [];
  const availableCourses = useMemo(() => {
    const selectedProgramIds = new Set((filters.program_ids || []).map(Number));
    if (!selectedProgramIds.size) return availableCoursesAll;
    return availableCoursesAll.filter((c) => selectedProgramIds.has(Number(c.program_id)));
  }, [availableCoursesAll, filters.program_ids]);

  const programSeries = useMemo(() => toSeriesRows(analytics.current?.programs?.timeline), [analytics.current]);
  const courseSeries = useMemo(() => toSeriesRows(analytics.current?.courses?.timeline), [analytics.current]);
  const insights = useMemo(() => buildComparisonInsights(analytics.current, analytics.comparison), [analytics]);

  const visibleProgramNames = useMemo(() => {
    if (programFocus) return [programFocus];
    const base = programSeries.names.filter((n) => !programHidden.has(n));
    if (filters.top_n === "all") return base;
    const n = Number(filters.top_n || 0);
    if (!n || n <= 0) return base;
    const topNames = new Set((analytics.current?.programs?.totals || []).slice(0, n).map((r) => r.name));
    return base.filter((name) => topNames.has(name));
  }, [analytics.current, filters.top_n, programFocus, programHidden, programSeries.names]);
  const visibleCourseNames = useMemo(() => {
    if (courseFocus) return [courseFocus];
    const base = courseSeries.names.filter((n) => !courseHidden.has(n));
    if (filters.top_n === "all") return base;
    const n = Number(filters.top_n || 0);
    if (!n || n <= 0) return base;
    const topNames = new Set((analytics.current?.courses?.totals || []).slice(0, n).map((r) => r.name));
    return base.filter((name) => topNames.has(name));
  }, [analytics.current, courseFocus, courseHidden, courseSeries.names, filters.top_n]);

  const programSeriesCumulative = useMemo(
    () => toCumulativeSeries(programSeries.data, visibleProgramNames),
    [programSeries.data, visibleProgramNames]
  );
  const courseSeriesCumulative = useMemo(
    () => toCumulativeSeries(courseSeries.data, visibleCourseNames),
    [courseSeries.data, visibleCourseNames]
  );

  const currentTotal = Number(analytics.current?.summary?.total_registrations || 0);
  const comparisonTotal = Number(analytics.comparison?.summary?.total_registrations || 0);
  const growthPct = comparisonTotal > 0 ? ((currentTotal - comparisonTotal) / comparisonTotal) * 100 : null;

  const handleApply = () => {
    trackInteraction("apply_filters");
    load(filters);
  };

  const handleReset = () => {
    const next = roleDefaultFilters(role);
    setFilters(next);
    setProgramHidden(new Set());
    setCourseHidden(new Set());
    setProgramFocus("");
    setCourseFocus("");
    trackInteraction("reset_filters");
    load(next);
  };

  const toggleHidden = (setter, name) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const saveCurrentView = () => {
    const name = saveName.trim();
    if (!name) {
      toast.info("Enter a view name first.");
      return;
    }
    const nextView = { id: `view_${Date.now()}`, name, filters };
    const next = [nextView, ...savedViews];
    setSavedViews(next);
    setSaveName("");
    setSelectedViewId(nextView.id);
    localStorage.setItem(viewsStorageKey, JSON.stringify(next));
    trackInteraction("save_view");
    toast.success("View saved");
  };

  const loadView = (viewId) => {
    setSelectedViewId(viewId);
    const v = savedViews.find((item) => item.id === viewId);
    if (!v) return;
    setFilters(v.filters);
    trackInteraction("load_view");
    load(v.filters);
  };

  const deleteView = () => {
    if (!selectedViewId) return;
    const next = savedViews.filter((v) => v.id !== selectedViewId);
    setSavedViews(next);
    setSelectedViewId("");
    localStorage.setItem(viewsStorageKey, JSON.stringify(next));
    if (localStorage.getItem(defaultViewKey) === selectedViewId) localStorage.removeItem(defaultViewKey);
    trackInteraction("delete_view");
    toast.success("View deleted");
  };

  const setDefaultView = () => {
    if (!selectedViewId) return;
    localStorage.setItem(defaultViewKey, selectedViewId);
    trackInteraction("set_default_view");
    toast.success("Default view updated");
  };

  const exportCsv = () => {
    const csv = buildCsvRows(analytics.current);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `academics_analytics_${filters.start_date || "start"}_${filters.end_date || "end"}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    trackInteraction("export_csv");
  };

  const safeExportPng = async (containerId, fileName) => {
    try {
      await exportChartPng(containerId, fileName);
      trackInteraction("export_png");
    } catch (_error) {
      toast.error("Failed to export PNG for this chart.");
    }
  };

  const lineChart = (seriesData, names, hiddenSet, onLegendToggle, containerId) => (
    <Box id={containerId}>
      <ResponsiveContainer width="100%" height={360}>
        <LineChart data={seriesData} margin={{ top: 16, right: 18, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.28)" vertical={false} />
          <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#cbd5e1" }} tickLine={{ stroke: "#cbd5e1" }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#cbd5e1" }} tickLine={{ stroke: "#cbd5e1" }} />
          <Tooltip content={<StyledTooltip />} />
          <Legend onClick={(payload) => payload?.dataKey && onLegendToggle(payload.dataKey)} />
          {names.map((name, idx) => (
            <Line
              key={name}
              hide={hiddenSet.has(name)}
              type="monotoneX"
              dataKey={name}
              stroke={PALETTE[idx % PALETTE.length]}
              dot={{ r: 0 }}
              activeDot={{ r: 4, strokeWidth: 0 }}
              strokeWidth={2.5}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
        Cumulative registrations across the selected time period.
      </Typography>
    </Box>
  );

  const totalsBar = (rows, color, containerId) => (
    <Box id={containerId}>
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={rows} margin={{ top: 10, right: 12, left: 0, bottom: 18 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.28)" vertical={false} />
          <XAxis
            dataKey="name"
            interval={0}
            tickFormatter={(v) => abbreviateLabel(v, 14)}
            angle={-24}
            textAnchor="end"
            height={86}
            tick={{ fontSize: 11, fill: "#64748b" }}
            axisLine={{ stroke: "#cbd5e1" }}
            tickLine={{ stroke: "#cbd5e1" }}
          />
          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#cbd5e1" }} tickLine={{ stroke: "#cbd5e1" }} />
          <Tooltip content={<StyledTooltip />} />
          <Bar dataKey="count" fill={color} radius={[8, 8, 0, 0]} barSize={22} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );

  return (
    <Box className={styles.root}>
      <Paper elevation={0} className={styles.contentPaper}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Box>
            <Typography variant="h4" className={styles.pageTitle}>
              Academics Analytics
            </Typography>
            <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
              Program and course registrations with smart insights
            </Typography>
          </Box>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            boxShadow: "0 8px 20px rgba(15,23,42,0.06)",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <DateRangeFilter value={filters} onChange={setFilters} />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Interval</InputLabel>
              <Select value={filters.interval || "day"} label="Interval" onChange={(e) => setFilters((p) => ({ ...p, interval: e.target.value }))}>
                {INTERVALS.map((v) => (
                  <MenuItem key={v} value={v}>
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Top N</InputLabel>
              <Select
                value={filters.top_n ?? 10}
                label="Top N"
                onChange={(e) =>
                  setFilters((p) => ({
                    ...p,
                    top_n: e.target.value === "all" ? "all" : Number(e.target.value),
                  }))
                }
              >
                {TOP_N_OPTIONS.map((v) => (
                  <MenuItem key={String(v)} value={v}>
                    {String(v).toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              multiple
              size="small"
              options={availablePrograms}
              getOptionLabel={(o) => o.name}
              value={availablePrograms.filter((p) => (filters.program_ids || []).includes(Number(p.id)))}
              onChange={(_, values) => setFilters((p) => ({ ...p, program_ids: values.map((v) => Number(v.id)), course_ids: [] }))}
              renderInput={(params) => <TextField {...params} label="Programs" />}
              sx={{ minWidth: 260 }}
            />
            <Autocomplete
              multiple
              size="small"
              options={availableCourses}
              getOptionLabel={(o) => o.name}
              value={availableCourses.filter((c) => (filters.course_ids || []).includes(Number(c.id)))}
              onChange={(_, values) => setFilters((p) => ({ ...p, course_ids: values.map((v) => Number(v.id)) }))}
              renderInput={(params) => <TextField {...params} label="Courses" />}
              sx={{ minWidth: 260 }}
            />

            <Stack direction="row" alignItems="center" spacing={1}>
              <Switch checked={Boolean(filters.compare)} onChange={(e) => setFilters((p) => ({ ...p, compare: e.target.checked }))} />
              <Typography variant="body2">Compare previous period</Typography>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleApply} disabled={loading}>
              Apply
            </Button>
            <Button variant="outlined" onClick={handleReset}>
              Reset
            </Button>
            {canExport && (
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv}>
                Export CSV
              </Button>
            )}
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>
              Print View
            </Button>
            <Chip label={`Latency: ${analytics.metadata?.elapsed_ms || 0} ms`} variant="outlined" />
            <Chip label={`Top filter: ${String(filters.top_n ?? 10).toUpperCase()}`} variant="outlined" />
          </Stack>

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField size="small" label="Save current view" value={saveName} onChange={(e) => setSaveName(e.target.value)} sx={{ minWidth: 220 }} />
            <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={saveCurrentView}>
              Save
            </Button>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Saved views</InputLabel>
              <Select label="Saved views" value={selectedViewId} onChange={(e) => loadView(e.target.value)}>
                <MenuItem value="">None</MenuItem>
                {savedViews.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button size="small" variant="outlined" onClick={setDefaultView} disabled={!selectedViewId}>
              Set as default
            </Button>
            <Button size="small" color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={deleteView} disabled={!selectedViewId}>
              Delete view
            </Button>
          </Stack>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <Card variant="outlined" sx={CHART_CARD_SX}>
              <CardHeader title="Current Total" />
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {currentTotal}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card variant="outlined" sx={CHART_CARD_SX}>
              <CardHeader title="Previous Total" />
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {analytics.comparison ? comparisonTotal : "-"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card variant="outlined" sx={CHART_CARD_SX}>
              <CardHeader title="Growth vs Previous" />
              <CardContent>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: growthPct == null ? "text.primary" : growthPct >= 0 ? "success.main" : "error.main" }}
                >
                  {growthPct == null ? "-" : `${growthPct.toFixed(1)}%`}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card variant="outlined" sx={CHART_CARD_SX}>
              <CardHeader title="Top Program" />
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {analytics.current?.programs?.totals?.[0]?.name || "-"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {insights.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {insights.map((ins, idx) => (
              <Alert key={idx} severity="info" sx={{ mb: 1 }}>
                {ins}
              </Alert>
            ))}
          </Box>
        )}

        {loading ? (
          <Box className={styles.loadingContainer}>
            <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2, mb: 2 }} />
            <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2, mb: 2 }} />
            <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2, mb: 2 }} />
            <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card variant="outlined" sx={CHART_CARD_SX}>
                <CardHeader
                  title="Program Registrations Trend (Cumulative)"
                  action={
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined" onClick={() => setProgramHidden(new Set())}>
                        Show all
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => setProgramHidden(new Set(programSeries.names))}>
                        Hide all
                      </Button>
                      {canExport && (
                        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => safeExportPng("chart_program_line", "program_trend.png")}>
                          PNG
                        </Button>
                      )}
                      <Button size="small" variant="outlined" startIcon={<OpenInFullIcon />} onClick={() => setFullscreen({ open: true, key: "program_line" })}>
                        Fullscreen
                      </Button>
                    </Stack>
                  }
                />
                <CardContent>
                  <Stack direction="row" spacing={2} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                    <Autocomplete
                      size="small"
                      options={["", ...programSeries.names]}
                      value={programFocus}
                      onChange={(_, value) => setProgramFocus(value || "")}
                      renderInput={(params) => <TextField {...params} label="Focus program (optional)" />}
                      sx={{ minWidth: 260 }}
                    />
                  </Stack>
                  {programSeriesCumulative.length === 0 || visibleProgramNames.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No program registration data in this range.
                    </Typography>
                  ) : (
                    lineChart(programSeriesCumulative, visibleProgramNames, programHidden, (name) => toggleHidden(setProgramHidden, name), "chart_program_line")
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined" sx={CHART_CARD_SX}>
                <CardHeader
                  title="Course Registrations Trend (Cumulative)"
                  action={
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined" onClick={() => setCourseHidden(new Set())}>
                        Show all
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => setCourseHidden(new Set(courseSeries.names))}>
                        Hide all
                      </Button>
                      {canExport && (
                        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => safeExportPng("chart_course_line", "course_trend.png")}>
                          PNG
                        </Button>
                      )}
                      <Button size="small" variant="outlined" startIcon={<OpenInFullIcon />} onClick={() => setFullscreen({ open: true, key: "course_line" })}>
                        Fullscreen
                      </Button>
                    </Stack>
                  }
                />
                <CardContent>
                  <Stack direction="row" spacing={2} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                    <Autocomplete
                      size="small"
                      options={["", ...courseSeries.names]}
                      value={courseFocus}
                      onChange={(_, value) => setCourseFocus(value || "")}
                      renderInput={(params) => <TextField {...params} label="Focus course (optional)" />}
                      sx={{ minWidth: 260 }}
                    />
                  </Stack>
                  {courseSeriesCumulative.length === 0 || visibleCourseNames.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No course registration data in this range.
                    </Typography>
                  ) : (
                    lineChart(courseSeriesCumulative, visibleCourseNames, courseHidden, (name) => toggleHidden(setCourseHidden, name), "chart_course_line")
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ ...CHART_CARD_SX, height: "100%" }}>
                <CardHeader
                  title="Program Totals (Selected Duration)"
                  action={
                    <Stack direction="row" spacing={1}>
                      {canExport && (
                        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => safeExportPng("chart_program_bar", "program_totals.png")}>
                          PNG
                        </Button>
                      )}
                      <Button size="small" variant="outlined" startIcon={<OpenInFullIcon />} onClick={() => setFullscreen({ open: true, key: "program_bar" })}>
                        Fullscreen
                      </Button>
                    </Stack>
                  }
                />
                <CardContent>
                  {analytics.current?.programs?.totals?.length ? (
                    totalsBar(analytics.current.programs.totals, "#2563eb", "chart_program_bar")
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No totals available.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ ...CHART_CARD_SX, height: "100%" }}>
                <CardHeader
                  title="Course Totals (Selected Duration)"
                  action={
                    <Stack direction="row" spacing={1}>
                      {canExport && (
                        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => safeExportPng("chart_course_bar", "course_totals.png")}>
                          PNG
                        </Button>
                      )}
                      <Button size="small" variant="outlined" startIcon={<OpenInFullIcon />} onClick={() => setFullscreen({ open: true, key: "course_bar" })}>
                        Fullscreen
                      </Button>
                    </Stack>
                  }
                />
                <CardContent>
                  {analytics.current?.courses?.totals?.length ? (
                    totalsBar(analytics.current.courses.totals, "#7c3aed", "chart_course_bar")
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No totals available.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Paper>

      <Dialog fullScreen open={fullscreen.open} onClose={() => setFullscreen({ open: false, key: "" })}>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button variant="contained" onClick={() => setFullscreen({ open: false, key: "" })}>
              Close
            </Button>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>
              Print
            </Button>
          </Stack>
          {fullscreen.key === "program_line" && lineChart(programSeriesCumulative, visibleProgramNames, programHidden, (name) => toggleHidden(setProgramHidden, name), "chart_program_line_full")}
          {fullscreen.key === "course_line" && lineChart(courseSeriesCumulative, visibleCourseNames, courseHidden, (name) => toggleHidden(setCourseHidden, name), "chart_course_line_full")}
          {fullscreen.key === "program_bar" && totalsBar(analytics.current?.programs?.totals || [], "#2563eb", "chart_program_bar_full")}
          {fullscreen.key === "course_bar" && totalsBar(analytics.current?.courses?.totals || [], "#7c3aed", "chart_course_bar_full")}
        </Box>
      </Dialog>
    </Box>
  );
}
